import affectionChatbotSource from "./fixtures/affection-chatbot.rqt?raw";
import dungeonExplorationSource from "./fixtures/dungeon-exploration.rqt?raw";
import escapeRoomSource from "./fixtures/escape-room.rqt?raw";
import greenBambooHallSource from "./fixtures/green-bamboo-hall.rqt?raw";
import hanbitFestivalSource from "./fixtures/hanbit-festival.rqt?raw";
import lanternRoadSource from "./fixtures/lantern-road.rqt?raw";
import mysteryEvidenceSource from "./fixtures/mystery-evidence.rqt?raw";
import missingManuscriptSource from "./fixtures/missing-manuscript.rqt?raw";
import signalRidgeSource from "./fixtures/signal-ridge.rqt?raw";

export type OfficialRqtSampleKind = "sample" | "playable-card";
export type OfficialRqtSampleCategory =
  | "escape"
  | "character"
  | "mystery"
  | "exploration"
  | "cyoa"
  | "survival"
  | "school"
  | "wuxia";
export type OfficialRqtSampleDifficulty = "beginner" | "intermediate";
export type OfficialRqtSampleExpectedType = "scene-aware-game";

export interface OfficialRqtSample {
  id: string;
  title: string;
  description: string;
  kind: OfficialRqtSampleKind;
  category: OfficialRqtSampleCategory;
  difficulty: OfficialRqtSampleDifficulty;
  playLength?: string;
  showcases?: string[];
  recommendedTheme?: "gamebook";
  filename: string;
  sourcePath: string;
  source: string;
  expectedType: OfficialRqtSampleExpectedType;
}

export const OFFICIAL_RQT_SAMPLES: OfficialRqtSample[] = [
  {
    id: "escape-room",
    title: "Escape Room Key",
    description: "A compact locked-room scene graph with clue gathering and choice navigation.",
    kind: "sample",
    category: "escape",
    difficulty: "beginner",
    filename: "escape-room.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/escape-room.rqt",
    source: escapeRoomSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "affection-chatbot",
    title: "Tea Shop Promise",
    description: "A character-focused affection scene with dialogue, state, and branching choices.",
    kind: "sample",
    category: "character",
    difficulty: "beginner",
    filename: "affection-chatbot.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/affection-chatbot.rqt",
    source: affectionChatbotSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "mystery-evidence",
    title: "Moonlit Evidence",
    description: "A mystery investigation sample built around evidence discovery and scene links.",
    kind: "sample",
    category: "mystery",
    difficulty: "beginner",
    filename: "mystery-evidence.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/mystery-evidence.rqt",
    source: mysteryEvidenceSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "dungeon-exploration",
    title: "Lantern Dungeon",
    description: "A dungeon exploration sample with route choices, danger, and encounter scenes.",
    kind: "sample",
    category: "exploration",
    difficulty: "beginner",
    filename: "dungeon-exploration.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/dungeon-exploration.rqt",
    source: dungeonExplorationSource,
    expectedType: "scene-aware-game",
  },
];

export const OFFICIAL_RQT_PLAYABLE_CARDS: OfficialRqtSample[] = [
  {
    id: "lantern-road",
    title: "Lantern Road",
    description: "A beginner-friendly branching text adventure about choosing a road at dusk.",
    kind: "playable-card",
    category: "cyoa",
    difficulty: "beginner",
    playLength: "5-10 min",
    showcases: ["stateful choices", "branching endings", "Gamebook Adventure Log"],
    recommendedTheme: "gamebook",
    filename: "lantern-road.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/lantern-road.rqt",
    source: lanternRoadSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "missing-manuscript",
    title: "The Missing Manuscript",
    description: "A literary mystery with clues, witness trust, and a final accusation.",
    kind: "playable-card",
    category: "mystery",
    difficulty: "intermediate",
    playLength: "10-15 min",
    showcases: ["clue tracking", "gated accusation", "witness trust"],
    recommendedTheme: "gamebook",
    filename: "missing-manuscript.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/missing-manuscript.rqt",
    source: missingManuscriptSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "signal-ridge",
    title: "Seven Days at Signal Ridge",
    description: "A survival simulation about food, warmth, signal strength, and rescue choices.",
    kind: "playable-card",
    category: "survival",
    difficulty: "intermediate",
    playLength: "10-15 min",
    showcases: ["resource pressure", "gated rescue", "stateful survival choices"],
    recommendedTheme: "gamebook",
    filename: "signal-ridge.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/signal-ridge.rqt",
    source: signalRidgeSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "hanbit-festival",
    title: "Festival Week at Hanbit Academy",
    description: "A SFW campus schedule sim about clubs, friendship, and festival prep.",
    kind: "playable-card",
    category: "school",
    difficulty: "beginner",
    playLength: "8-12 min",
    showcases: ["schedule tradeoffs", "relationship pressure", "stress management"],
    recommendedTheme: "gamebook",
    filename: "hanbit-festival.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/hanbit-festival.rqt",
    source: hanbitFestivalSource,
    expectedType: "scene-aware-game",
  },
  {
    id: "green-bamboo-hall",
    title: "First Sword of Green Bamboo Hall",
    description: "An original wuxia training card about discipline, reputation, and a sect trial.",
    kind: "playable-card",
    category: "wuxia",
    difficulty: "intermediate",
    playLength: "10-15 min",
    showcases: ["discipline tradeoffs", "reputation gates", "sect trial ending"],
    recommendedTheme: "gamebook",
    filename: "green-bamboo-hall.rqt",
    sourcePath: "packages/core/src/roqeet/fixtures/green-bamboo-hall.rqt",
    source: greenBambooHallSource,
    expectedType: "scene-aware-game",
  },
];

export const OFFICIAL_RQT_CATALOG: OfficialRqtSample[] = [
  ...OFFICIAL_RQT_SAMPLES,
  ...OFFICIAL_RQT_PLAYABLE_CARDS,
];

export function getOfficialRqtSample(id: string): OfficialRqtSample | undefined {
  return OFFICIAL_RQT_CATALOG.find((sample) => sample.id === id);
}
